import React, { useEffect, useState } from "react";
import {
  Upload,
  Button,
  Modal,
  Input,
  message,
  Typography,
  Space,
  Card,
  List,
  notification,
  Grid,
  Tag,
  Tooltip,
} from "antd";
import {
  UploadOutlined,
  EditOutlined,
  DeleteOutlined,
  DownloadOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";
import TitleComponent from "../components/TitleComponent";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import "moment/locale/de";

moment.locale("de");

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const Files = () => {
  const screens = useBreakpoint();
  const [fileList, setFileList] = useState([]);
  const [zipFiles, setZipFiles] = useState([]);
  const [renameModalVisible, setRenameModalVisible] = useState(false);
  const [fileToRename, setFileToRename] = useState(null);
  const [newFileName, setNewFileName] = useState("");
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [switchModalVisible, setSwitchModalVisible] = useState(false);
  const [renameUploadVisible, setRenameUploadVisible] = useState(false);
  const [uploadConfirmVisible, setUploadConfirmVisible] = useState(false);
  const [newZipName, setNewZipName] = useState(null);
  const [api, contextHolder] = notification.useNotification();
  const navigate = useNavigate();

  // Feststellen, ob wir uns im mobilen Ansichtsmodus befinden
  const isMobile = !screens.md;

  const fetchZipFiles = () => {
    // Verwenden der API mit Datumsangaben
    axios
      .get(`${process.env.REACT_APP_API_URL}/files-with-date`)
      .then((res) => setZipFiles(res.data.uploads || []))
      .catch(() => message.error("Fehler beim Laden der ZIP-Dateien."));
  };

  useEffect(() => {
    fetchZipFiles();
  }, []);

  const uploadProps = {
    beforeUpload: (file) => {
      setFileList([file]);
      setRenameUploadVisible(true);
      return false;
    },
    fileList,
    accept: ".zip",
    multiple: false,
    showUploadList: false,
  };

  useEffect(() => {
    if (fileList.length > 0 && !newZipName) {
      const nameWithoutZip = fileList[0].name.replace(/\.zip$/i, "");
      setNewZipName(nameWithoutZip);
    }
  }, [fileList, newZipName]);

  const handleUpload = async () => {
    if (fileList.length === 0)
      return message.error("Bitte wähle eine ZIP-Datei aus!");

    const formData = new FormData();
    formData.append("file", fileList[0]);
    const safeName = newZipName.endsWith(".zip")
      ? newZipName
      : `${newZipName}.zip`;
    formData.append("newZipName", safeName);

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/upload`,
        formData
      );
      if (res.data?.zipFile) {
        message.success("ZIP-Datei erfolgreich hochgeladen!");
        fetchZipFiles();
        setFileList([]);
        setNewZipName(null);
        setSwitchModalVisible(true);
      }
    } catch (err) {
      if (err.response && err.response.status === 409) {
        api.error({
          message: "Datei bereits vorhanden",
          description: `Eine ZIP-Datei mit dem Namen "${safeName}" existiert bereits.`,
          placement: "topRight",
        });
      } else {
        message.error("Upload fehlgeschlagen.");
      }
    }
  };

  const handleRename = () => {
    const safeNewName = newFileName.endsWith(".zip")
      ? newFileName
      : `${newFileName}.zip`;
    axios
      .put(`${process.env.REACT_APP_API_URL}/rename`, {
        oldName: fileToRename,
        newName: safeNewName,
      })
      .then(() => {
        message.success("ZIP-Datei erfolgreich umbenannt.");
        fetchZipFiles();
      })
      .catch(() => message.error("Fehler beim Umbenennen."))
      .finally(() => setRenameModalVisible(false));
  };

  const handleDelete = () => {
    axios
      .delete(
        `${process.env.REACT_APP_API_URL}/delete/${encodeURIComponent(
          fileToDelete
        )}`
      )
      .then(() => {
        message.success("ZIP-Datei gelöscht.");
        fetchZipFiles();
      })
      .catch(() => message.error("Fehler beim Löschen."))
      .finally(() => setDeleteModalVisible(false));
  };

  const handleDownload = (file) => {
    axios({
      url: `${process.env.REACT_APP_API_URL}/download/${encodeURIComponent(
        file
      )}`,
      method: "GET",
      responseType: "blob",
    })
      .then((res) => {
        const href = URL.createObjectURL(res.data);
        const link = document.createElement("a");
        link.href = href;
        link.setAttribute("download", file);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(href);
      })
      .catch(() => message.error("Fehler beim Download."));
  };

  // Format des Datums für die Anzeige
  const formatDate = (dateString) => {
    const date = moment(dateString);
    if (!date.isValid()) return "Unbekanntes Datum";

    // Für mobile Ansicht kürzeres Format
    if (isMobile) {
      return date.format("DD.MM.YY HH:mm");
    }

    // Für Desktop vollständigeres Format
    return date.format("DD.MM.YYYY HH:mm:ss");
  };

  return (
    <Space
      direction="vertical"
      style={{ width: "100%", padding: isMobile ? "16px" : "24px" }}
      size="large"
    >
      {contextHolder}
      <TitleComponent
        title="ZIP-Dateien"
        subTitle="ZIP-Dateien verwalten und hochladen"
        breadcrumbItems={[
          { path: "/", label: "Home" },
          { path: "/files", label: "Dateien" },
        ]}
      />

      <Card title={<Title level={4}>ZIP-Datei hochladen</Title>}>
        <Upload.Dragger {...uploadProps}>
          <p className="ant-upload-drag-icon">
            <UploadOutlined />
          </p>
          <p className="ant-upload-text">Datei hierher ziehen oder klicken</p>
          <p className="ant-upload-hint">Nur eine .zip-Datei zulässig</p>
        </Upload.Dragger>
      </Card>

      <List
        header={<Title level={5}>Vorhandene ZIP-Dateien</Title>}
        itemLayout="horizontal"
        dataSource={zipFiles}
        renderItem={(file) => (
          <List.Item
            actions={[
              <Button
                shape="circle"
                icon={<EditOutlined />}
                onClick={() => {
                  // Hier nur den Dateinamen ohne Datum verwenden
                  setFileToRename(file.filename);
                  setNewFileName(file.filename.replace(/\.zip$/i, "")); // Vorsichtshalber .zip case-insensitive
                  setRenameModalVisible(true);
                }}
              />,
              <Button
                shape="circle"
                danger
                icon={<DeleteOutlined />}
                onClick={() => {
                  // Hier nur den Dateinamen ohne Datum verwenden
                  setFileToDelete(file.filename);
                  setDeleteModalVisible(true);
                }}
              />,
              <Button
                shape="circle"
                icon={<DownloadOutlined />}
                onClick={() => handleDownload(file.filename)} // Hier nur den Dateinamen ohne Datum verwenden
              />,
            ]}
          >
            <List.Item.Meta
              title={file.filename}
              description={
                <Space>
                  <ClockCircleOutlined style={{ color: "#1677ff" }} />
                  <Tooltip title={formatDate(file.uploaded_at)}>
                    <Tag color="blue">{formatDate(file.uploaded_at)}</Tag>
                  </Tooltip>
                </Space>
              }
            />
          </List.Item>
        )}
      />

      <Modal
        title="ZIP umbenennen?"
        open={renameUploadVisible}
        onOk={() => {
          setRenameUploadVisible(false);
          setUploadConfirmVisible(true);
        }}
        onCancel={() => {
          setRenameUploadVisible(false);
          setUploadConfirmVisible(true);
        }}
        okText="Umbenennen"
        cancelText="Nicht umbenennen"
      >
        <Input
          value={newZipName}
          onChange={(e) => {
            const value = e.target.value.replace(/\.zip$/i, "");
            setNewZipName(value);
          }}
          addonAfter=".zip"
        />
      </Modal>

      <Modal
        title="Upload bestätigen"
        open={uploadConfirmVisible}
        onOk={() => {
          setUploadConfirmVisible(false);
          handleUpload();
        }}
        onCancel={() => setUploadConfirmVisible(false)}
        okText="Hochladen"
        cancelText="Abbrechen"
      >
        <Text>Möchten Sie die Datei jetzt hochladen?</Text>
      </Modal>

      <Modal
        title="ZIP umbenennen"
        open={renameModalVisible}
        onOk={handleRename}
        onCancel={() => setRenameModalVisible(false)}
        okText="Umbenennen"
        cancelText="Abbrechen"
      >
        <Input
          value={newFileName}
          onChange={(e) => {
            const value = e.target.value.replace(/\.zip$/i, "");
            setNewFileName(value);
          }}
          addonAfter=".zip"
        />
      </Modal>

      <Modal
        title="ZIP löschen?"
        open={deleteModalVisible}
        onOk={handleDelete}
        onCancel={() => setDeleteModalVisible(false)}
        okText="Löschen"
        cancelText="Abbrechen"
      >
        <Text>
          Möchten Sie die Datei <strong>{fileToDelete}</strong> wirklich
          löschen?
        </Text>
      </Modal>

      <Modal
        title="ZIP erfolgreich hochgeladen!"
        open={switchModalVisible}
        onOk={() => {
          setSwitchModalVisible(false);
          navigate("/generator");
        }}
        onCancel={() => setSwitchModalVisible(false)}
        okText="Zum Generator"
        cancelText="Später"
      >
        <Text>Möchten Sie jetzt zum Dokumenten-Generator wechseln?</Text>
      </Modal>
    </Space>
  );
};

export default Files;
